import type {
  ServiceAccount,
  UpdateServiceAccountRequest,
} from '@compass/client/types/service_account';
import { useUpdateServiceAccount } from '@hooks/useServiceAccounts';
import { getErrorMessage } from '@utils/errorMessages';
import { AlertCircle, Loader2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

interface EditServiceAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  serviceAccount: ServiceAccount;
}

type ExpiryChoice = 'keep' | '30' | '60' | '90' | 'never';

const PRESETS: { value: ExpiryChoice; label: string }[] = [
  { value: 'keep', label: 'Keep current' },
  { value: '30', label: '30 days' },
  { value: '60', label: '60 days' },
  { value: '90', label: '90 days' },
  { value: 'never', label: 'Never expires' },
];

function computeExpiresAt(choice: ExpiryChoice): string | undefined {
  if (choice === 'keep' || choice === 'never') return undefined;
  const days = Number(choice);
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString();
}

export function EditServiceAccountDialog({
  open,
  onOpenChange,
  serviceAccount,
}: EditServiceAccountDialogProps) {
  const [name, setName] = useState(serviceAccount.name ?? '');
  const [description, setDescription] = useState(serviceAccount.description ?? '');
  const [expiryChoice, setExpiryChoice] = useState<ExpiryChoice>('keep');

  const updateMutation = useUpdateServiceAccount();

  useEffect(() => {
    if (open) {
      setName(serviceAccount.name ?? '');
      setDescription(serviceAccount.description ?? '');
      setExpiryChoice('keep');
      updateMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, serviceAccount.id]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!serviceAccount.id) return;
    const trimmedName = name.trim();
    if (!trimmedName) return;

    const request: UpdateServiceAccountRequest = {
      service_account_id: serviceAccount.id,
    };

    if (trimmedName !== (serviceAccount.name ?? '')) {
      request.name = trimmedName;
    }
    const trimmedDescription = description.trim();
    if (trimmedDescription !== (serviceAccount.description ?? '')) {
      request.description = trimmedDescription;
    }
    if (expiryChoice === 'never') {
      request.clear_expires_at = true;
    } else if (expiryChoice !== 'keep') {
      request.expires_at = computeExpiresAt(expiryChoice);
    }

    updateMutation.mutate(request, {
      onSuccess: () => {
        toast.success('Service account updated');
        onOpenChange(false);
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Service Account</DialogTitle>
          <DialogDescription>
            Update the name, description, or expiration. The access key ID and secret are immutable
            — use Rotate to change the secret.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="edit-sa-name">Name</Label>
            <Input
              id="edit-sa-name"
              value={name}
              onChange={e => setName(e.target.value)}
              autoFocus
              maxLength={64}
              disabled={updateMutation.isPending}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-sa-description">Description</Label>
            <Textarea
              id="edit-sa-description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={2}
              maxLength={256}
              disabled={updateMutation.isPending}
            />
          </div>

          <div className="space-y-2">
            <Label>Expiration</Label>
            <div className="flex flex-wrap gap-2">
              {PRESETS.map(preset => (
                <Button
                  key={preset.value}
                  type="button"
                  variant={expiryChoice === preset.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setExpiryChoice(preset.value)}
                  disabled={updateMutation.isPending}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
            {expiryChoice !== 'keep' && expiryChoice !== 'never' && (
              <p className="text-xs text-muted-foreground">
                New expiration: {new Date(computeExpiresAt(expiryChoice)!).toLocaleDateString()}.
              </p>
            )}
            {expiryChoice === 'never' && (
              <p className="text-xs text-muted-foreground">
                Expiration will be cleared — this account will not expire.
              </p>
            )}
          </div>

          {updateMutation.isError && (
            <div className="bg-destructive/10 border border-destructive/50 text-destructive px-4 py-3 rounded-md text-sm">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="font-medium">Failed to update</p>
                  <p className="mt-1">
                    {getErrorMessage(updateMutation.error) || 'Please try again.'}
                  </p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={updateMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || updateMutation.isPending}>
              {updateMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
